import { Card } from "antd";
import {
  EyeOutlined,
  HeartOutlined,
  CommentOutlined,
  ShareAltOutlined,
  StarOutlined,
} from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { formatCnt } from "../../utils/formatCnt";

const CustomArrow = ({ direction, onClick, disabled }) => {
  if (disabled) return null;
  return (
    <button
      onClick={onClick}
      className={`absolute top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/40 hover:bg-black/60 transition-all duration-200 flex items-center justify-center border-none cursor-pointer shadow-md ${
        direction === "prev" ? "left-2" : "right-2"
      }`}
    >
      <FontAwesomeIcon
        icon={direction === "prev" ? faChevronLeft : faChevronRight}
        className="text-white text-lg"
      />
    </button>
  );
};

export const RecentVideosCarousel = ({ videos = [], influencerId }) => {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);

  const updateArrowState = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeft(scrollLeft > 10);
      setShowRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const { clientWidth } = scrollRef.current;
      const moveDistance =
        direction === "left" ? -clientWidth * 0.9 : clientWidth * 0.9;
      scrollRef.current.scrollBy({ left: moveDistance, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const ref = scrollRef.current;
    if (!ref) return;

    ref.addEventListener("scroll", updateArrowState);
    updateArrowState();

    return () => {
      ref.removeEventListener("scroll", updateArrowState);
    };
  }, [videos]);

  return (
    <Card
      title={<span className="text-[18px] font-semibold">최근 영상</span>}
      className="rounded-xl border border-[#E8E8E8] overflow-hidden"
      styles={{ body: { padding: 0 } }}
    >
      <div className="relative group px-4 md:px-10 py-5">
        <div
          ref={scrollRef}
          className="flex overflow-x-auto scroll-smooth no-scrollbar snap-x snap-mandatory gap-[12px]"
        >
          {videos.map((video, idx) => {
            const videoId = video.item_id || video.video_id;
            return (
              <div
                key={idx}
                className="video-card-wrapper flex-shrink-0 snap-start py-2"
              >
                <Card
                  className="rounded-lg border border-[#E8E8E8] shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer"
                  styles={{ body: { padding: 10 } }}
                  onClick={() => {
                    if (videoId && influencerId) {
                      window.open(
                        `https://www.tiktok.com/@${influencerId}/video/${videoId}`,
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }
                  }}
                  cover={
                    <div className="w-full relative bg-gray-100 aspect-[2/3]">
                      <img
                        alt="thumbnail"
                        src={video.thumbnail_url || video.thumbnail}
                        className="absolute inset-0 w-full h-full object-cover rounded-t-lg transition-transform duration-300"
                      />
                    </div>
                  }
                >
                  <div className="text-[10px] md:text-[12px] text-[#595959] space-y-1">
                    <p className="flex items-center gap-1.5 m-0 truncate">
                      <EyeOutlined className="text-[#13C2C2]" />
                      <span>
                        {formatCnt(video.video_views || video.play_count || 0)}
                      </span>
                    </p>
                    <p className="flex items-center gap-1.5 m-0 truncate">
                      <HeartOutlined className="text-[#FF4D4F]" />
                      <span>
                        {formatCnt(
                          video.likes ||
                            video.like_count ||
                            video.digg_count ||
                            0
                        )}
                      </span>
                    </p>
                    <p className="flex items-center gap-1.5 m-0 truncate">
                      <CommentOutlined className="text-[#1890FF]" />
                      <span>
                        {formatCnt(video.comments || video.comment_count || 0)}
                      </span>
                    </p>
                    <p className="flex items-center gap-1.5 m-0 truncate">
                      <ShareAltOutlined className="text-[#52C41A]" />
                      <span>
                        {formatCnt(video.shares || video.share_count || 0)}
                      </span>
                    </p>
                    <p className="flex items-center gap-1.5 m-0 truncate">
                      <StarOutlined className="text-[#FAAD14]" />
                      <span>
                        {formatCnt(video.favorites || video.collect_count || 0)}
                      </span>
                    </p>
                    <p className="text-[9px] md:text-[11px] mt-[6px] md:mt-[8px] mb-0 text-[#1890FF] overflow-hidden text-ellipsis whitespace-nowrap font-medium min-h-[16px] md:min-h-[20px]">
                      {video.hashtag_name || video.caption}
                    </p>
                  </div>
                </Card>
              </div>
            );
          })}
        </div>

        <CustomArrow
          direction="prev"
          onClick={() => scroll("left")}
          disabled={!showLeft}
        />
        <CustomArrow
          direction="next"
          onClick={() => scroll("right")}
          disabled={!showRight}
        />
      </div>
    </Card>
  );
};
